import { useQuery } from "@apollo/client";
import { ChevronDownIcon, ChevronUpIcon } from "@heroicons/react/outline";
import { isNil, uniq } from "lodash";
import moment from "moment";
import { useState } from "react";
import store from "store2";
import {
  ArtifactType,
  CreatedArtifactFragmentFragment,
  FeedbackState,
  GetProfileCoachingInsightsQueryQuery,
  GetProfileCoachingInsightsQueryQueryVariables,
  ManagerInsightType,
  ProfileCoachingInsightFragmentFragment,
} from "types/graphql-schema";
import { BasicUser } from "types/topicflow";

import AddTopicToMeetingDialog from "@apps/add-topic-to-meeting-dialog/add-topic-to-meeting-dialog";
import ArtifactCreationDialog from "@apps/artifact-creation-dialog/artifact-creation-dialog";
import { InsightCardData } from "@apps/profile/coaching-tab/coaching-dimensions";
import useLabel from "@apps/use-label/use-label";
import { currentUserVar } from "@cache/cache";
import Button from "@components/button/button";
import { ButtonSize, buttonTheme } from "@components/button/button-constants";
import Dropdown from "@components/dropdown/dropdown";
import Loading from "@components/loading/loading";
import Modal from "@components/modal/modal";
import ModalTitle from "@components/modal/modal-title";
import CustomTextareaAutosize from "@components/textarea-autosize/textarea-autosize";
import { TopicflowIcon } from "@components/topicflow-icon/topicflow-icon";
import { classNames } from "@helpers/css";

import ProfileEmptyState from "../../profile-empty-state";
import getProfileArtifactsQuery from "../graphql/get-profile-artifacts-query";
import getProfileCoachingInsightsQuery from "../graphql/get-profile-coaching-insights-query";

export type CoachingInsightsVariant = "default" | "embedded";

const CoachingInsight = ({
  insight,
  profile,
  onDismiss,
  variant = "default",
}: {
  insight: ProfileCoachingInsightFragmentFragment;
  profile: BasicUser;
  onDismiss: (
    coachingReportId: number | null | undefined,
    insightTitle: string,
  ) => void;
  variant?: CoachingInsightsVariant;
}) => {
  const [isExpanded, setIsExpanded] = useState(false);
  const [dismissOtherReason, setDismissOtherReason] = useState("");
  const [showDismissOtherReasonDialog, setShowDismissOtherReasonDialog] =
    useState(false);
  const [showActionDialog, setShowActionDialog] = useState(false);
  const currentUser = currentUserVar();
  const label = useLabel();
  const topicNotes = (insight.coachingInsight?.topics?.notes || []).filter(
    (n): n is string => n !== null,
  );

  const actionButtonText =
    insight.insightType === ManagerInsightType.Feedback
      ? `Request ${label("feedback")}`
      : insight.insightType === ManagerInsightType.Recognition
        ? `Give ${label("recognition")}`
        : "Add";

  const handleClickDismiss = () => {
    setShowDismissOtherReasonDialog(false);
    onDismiss(insight.coachingReportId, insight.title);
  };

  const handleCreateArtifact = (
    artifact: CreatedArtifactFragmentFragment | undefined,
  ) => {
    if (artifact) {
      handleClickDismiss();
    }
    setShowActionDialog(false);
  };

  const handleCreatedMeetingTopic = () => {
    handleClickDismiss();
    setShowActionDialog(false);
  };

  return (
    <div className="rounded text-sm">
      {/* Row */}
      <div
        className={classNames(
          "flex items-start gap-2 px-3 py-2 cursor-pointer hover:bg-gray-50 rounded-[6px] transition-colors",
          variant === "embedded" && "-mx-3",
        )}
        onClick={() => setIsExpanded((prev) => !prev)}
      >
        <TopicflowIcon
          type="ai"
          className="w-4 h-4 text-violet-500 shrink-0 mt-0.5"
        />
        <span className="flex-1 font-medium text-gray-800">
          {insight.title}
        </span>
        {variant === "default" && (
          <div
            className="flex items-center gap-2 shrink-0"
            onClick={(e) => e.stopPropagation()}
          >
            <Dropdown
              options={[
                {
                  label: "I've already handled this insight externally",
                  onClick: handleClickDismiss,
                },
                {
                  label: "The insight was irrelevant",
                  onClick: handleClickDismiss,
                },
                {
                  label: "The insight was inaccurate or incorrect",
                  onClick: handleClickDismiss,
                },
                {
                  label: "I don't want insights related to this topic",
                  onClick: handleClickDismiss,
                },
                {
                  label: "Other reason",
                  onClick: () => setShowDismissOtherReasonDialog(true),
                },
              ]}
            >
              <Button
                text="Dismiss"
                theme={buttonTheme.text}
                size={ButtonSize.medium}
              />
            </Dropdown>
          </div>
        )}
        {variant === "embedded" ? (
          isExpanded ? (
            <ChevronUpIcon className="h-4 w-4 text-gray-500 shrink-0 mt-0.5" />
          ) : (
            <ChevronDownIcon className="h-4 w-4 text-gray-500 shrink-0 mt-0.5" />
          )
        ) : (
          <Button
            theme={buttonTheme.iconGray}
            size={ButtonSize.medium}
            square
            icon={isExpanded ? ChevronUpIcon : ChevronDownIcon}
          />
        )}
      </div>

      {/* Expanded content */}
      {isExpanded &&
        (insight.insightType === ManagerInsightType.DiscussionTopic ? (
          insight.coachingInsight ? (
            <div className={classNames(variant === "embedded" && "-mx-3")}>
              <InsightCardData
                isOpen={isExpanded}
                insight={insight.coachingInsight}
                actionButton={
                  <Button
                    theme={buttonTheme.primary}
                    size={ButtonSize.medium}
                    onClick={() => setShowActionDialog(true)}
                  >
                    Add as topic in {label("1-on-1")}
                  </Button>
                }
              />
            </div>
          ) : null
        ) : insight.description ? (
          <div
            className={classNames("flex", variant === "embedded" && "-mx-3")}
          >
            <div className="w-6 shrink-0 flex justify-center">
              <div className="w-px h-full bg-gray-200" />
            </div>
            <div className="flex-1 min-w-0 flex flex-col gap-4">
              {insight.description && (
                <div className="flex flex-col gap-1">
                  <span className="text-gray-500 font-medium">Context</span>
                  <p className="text-gray-600 whitespace-pre-line">
                    {insight.description}
                  </p>
                </div>
              )}
              <div>
                <Button
                  theme={buttonTheme.primary}
                  size={ButtonSize.medium}
                  onClick={() => setShowActionDialog(true)}
                >
                  {actionButtonText}
                </Button>
              </div>
            </div>
          </div>
        ) : null)}

      {/* Dismiss other reason dialog */}
      {showDismissOtherReasonDialog && (
        <Modal
          alignedTop
          onClose={() => setShowDismissOtherReasonDialog(false)}
        >
          <div className="flex flex-col gap-6 p-6">
            <ModalTitle onClose={() => setShowDismissOtherReasonDialog(false)}>
              Other Reason
            </ModalTitle>
            <div>
              <CustomTextareaAutosize
                value={dismissOtherReason}
                onChange={(e) => setDismissOtherReason(e.target.value)}
                placeholder="Write your reason here..."
                className="w-full text-sm border rounded-md px-2 py-1"
              />
            </div>
            <div className="flex items-center gap-4 justify-end">
              <Button
                text="Cancel"
                onClick={() => setShowDismissOtherReasonDialog(false)}
              />
              <Button
                text="Dismiss this insight"
                theme={buttonTheme.primary}
                onClick={handleClickDismiss}
                disabled={dismissOtherReason.trim().length === 0}
              />
            </div>
          </div>
        </Modal>
      )}

      {/* Action dialogs */}
      {showActionDialog &&
        insight.insightType === ManagerInsightType.Feedback && (
          <ArtifactCreationDialog
            formOptions={{
              artifactType: ArtifactType.Feedback,
              feedbackRecipients: [profile],
              feedbackSenders: [],
              feedbackState: FeedbackState.Requested,
              feedbackRecipientsManagersCanView: true,
              feedbackRecipientsCanView: false,
              feedbackCreatorCanView: true,
              feedbackAdminsCanView: false,
              title: insight.suggestedArtifactTitle || insight.title,
            }}
            refetchQueries={[getProfileArtifactsQuery]}
            onClose={handleCreateArtifact}
          />
        )}
      {showActionDialog &&
        insight.insightType === ManagerInsightType.Recognition && (
          <ArtifactCreationDialog
            formOptions={{
              artifactType: ArtifactType.Recognition,
              recognitionRecipients: [profile],
              recognitionCoreValueId: insight.suggestedCoreValue?.id,
              title: insight.suggestedArtifactTitle || insight.title,
            }}
            refetchQueries={[getProfileArtifactsQuery]}
            onClose={handleCreateArtifact}
          />
        )}
      {showActionDialog &&
        insight.insightType === ManagerInsightType.DiscussionTopic && (
          <AddTopicToMeetingDialog
            variables={{
              startDatetimeAfter: moment().startOf("hour").format(),
              participants: [currentUser.id, profile.id],
              isFormalOneonone: true,
            }}
            topicTitle={insight.coachingInsight?.topics?.title || insight.title}
            topicNotes={topicNotes}
            onClose={() => setShowActionDialog(false)}
            onCreatedTopic={handleCreatedMeetingTopic}
          />
        )}
    </div>
  );
};

const storageKey = "coaching-insights-dismissed";

const getInsightKey = (
  coachingReportId: number | null | undefined,
  title: string,
) => `${coachingReportId ?? "unknown"}:${title}`;

const ProfileCoachingInsights = ({
  profile,
  alwaysShowPanel = true,
  variant = "default",
}: {
  profile: BasicUser;
  alwaysShowPanel?: boolean;
  variant?: CoachingInsightsVariant;
}) => {
  const currentUser = currentUserVar();
  const isReport = currentUser?.directReports.edges.some(
    (report) => report?.node?.id === profile.id,
  );
  const [dismissedInsights, setDismissedInsights] = useState<string[]>(
    () => store(storageKey) || [],
  );

  const { data, loading } = useQuery<
    GetProfileCoachingInsightsQueryQuery,
    GetProfileCoachingInsightsQueryQueryVariables
  >(getProfileCoachingInsightsQuery, {
    variables: { profileId: profile.id },
    skip: !isReport,
  });

  const [isCollapsed, setIsCollapsed] = useState(false);

  if (!isReport) {
    return null;
  }

  const handleDismissInsight = (
    coachingReportId: number | null | undefined,
    insightTitle: string,
  ) => {
    const key = getInsightKey(coachingReportId, insightTitle);
    const updated = uniq([...dismissedInsights, key]);
    store(storageKey, updated);
    setDismissedInsights(updated);
  };

  const insights = (data?.user?.managerInsights?.edges || [])
    .map((edge) => edge?.node)
    .filter((insight) => !isNil(insight))
    .filter(
      (insight) =>
        !dismissedInsights.includes(
          getInsightKey(insight.coachingReportId, insight.title),
        ),
    );

  if (insights.length === 0 && !alwaysShowPanel) {
    return null;
  }

  if (variant === "embedded") {
    if (loading && !data) {
      return <Loading />;
    }
    if (insights.length === 0) {
      return <ProfileEmptyState>No coaching insights.</ProfileEmptyState>;
    }
    return (
      <div className="flex flex-col gap-0 -mt-2">
        {insights.map((insight) => (
          <CoachingInsight
            key={`${insight.coachingReportId}-${insight.title}`}
            insight={insight}
            profile={profile}
            onDismiss={handleDismissInsight}
            variant="embedded"
          />
        ))}
      </div>
    );
  }

  return (
    <div className="flex flex-col gap-0 bg-white border border-gray-200 rounded-lg px-3 py-4 text-sm">
      {/* Header */}
      <div
        className="flex items-center justify-between px-3 py-2 cursor-pointer hover:bg-gray-50 rounded-[6px] transition-colors"
        onClick={() => setIsCollapsed((prev) => !prev)}
      >
        <span className="tf-header-base-dark">Manager Coaching Insights</span>
        <div className="flex items-center gap-1.5 text-gray-500">
          <TopicflowIcon type="eyeOff" className="w-4 h-4" />
          <span className="text-xs">Only visible to you</span>
          <Button
            className="ml-2"
            theme={buttonTheme.iconGray}
            size={ButtonSize.medium}
            square
            icon={isCollapsed ? ChevronDownIcon : ChevronUpIcon}
          />
        </div>
      </div>

      {/* Insights list */}
      {!isCollapsed &&
        (loading && !data ? (
          <Loading className="" />
        ) : insights.length > 0 ? (
          <div className="flex flex-col gap-0">
            {insights.map((insight) => (
              <CoachingInsight
                key={`${insight.coachingReportId}-${insight.title}`}
                insight={insight}
                profile={profile}
                onDismiss={handleDismissInsight}
              />
            ))}
          </div>
        ) : (
          <div className="p-2">
            <ProfileEmptyState>No coaching insights.</ProfileEmptyState>
          </div>
        ))}
    </div>
  );
};

export default ProfileCoachingInsights;
